/**
 * Decide what to do with a set of dropped files.
 *
 * Pure, and separate from the drop zone, because the interesting decisions are
 * not DOM events: how many runs are here, can their order be established, and
 * when must a person be asked rather than guessed at.
 *
 * The governing rule is that getting reference and candidate the wrong way
 * round inverts every finding in the run. So an order is only ever asserted
 * from evidence — the timestamps the pipeline puts in its filenames — and never
 * from the order files happened to arrive in. When the names do not settle it,
 * the pair is still offered, flagged as unverified, and the existing export-date
 * check and swap badge take over from there.
 */

import { listRuns, newestPair, orderRunPair, parseRunFilename, type RunFilename } from "../../engine/run-filename";

export type RunFile = { file: File; run: RunFilename };

export type PairChoice =
  /** Two files with a settled direction, ready to load. */
  | { kind: "pair"; reference: File; candidate: File; orderedBy: "filename" | "arrival" }
  /** More runs than a pair; the person picks, with the usual comparison offered. */
  | { kind: "choose"; runs: RunFile[]; suggested: { reference: File; candidate: File } }
  /** Nothing usable, with a reason worth showing rather than a silent no-op. */
  | { kind: "none"; reason: string };

const isJson = (file: File) => file.name.toLowerCase().endsWith(".json");

export function choosePair(files: File[]): PairChoice {
  const jsonFiles = files.filter(isJson);

  if (jsonFiles.length === 0) {
    return {
      kind: "none",
      reason:
        files.length === 0
          ? "No files were dropped."
          : "None of those files are JSON exports. Drop the .json files the pipeline produced."
    };
  }
  if (jsonFiles.length === 1) {
    return {
      kind: "none",
      reason: "Only one export was dropped. A comparison needs two runs of the same source."
    };
  }

  if (jsonFiles.length === 2) {
    const [first, second] = jsonFiles as [File, File];
    const left = parseRunFilename(first.name);
    const right = parseRunFilename(second.name);
    if (left !== null && right !== null) {
      const ordered = orderRunPair(left, right);
      if (ordered !== null) {
        // The older run is the reference. Established from the names, not assumed.
        const byName = new Map([
          [first.name, first],
          [second.name, second]
        ]);
        return {
          kind: "pair",
          reference: byName.get(ordered.reference.fileName)!,
          candidate: byName.get(ordered.candidate.fileName)!,
          orderedBy: "filename"
        };
      }
    }
    // The names settle nothing. Load them anyway — the export-date check reads
    // the files themselves, and the swap badge is one click — but say the order
    // is unverified rather than implying it was worked out.
    return { kind: "pair", reference: first, candidate: second, orderedBy: "arrival" };
  }

  const runs = listRuns(jsonFiles.map((file) => file.name));
  if (runs.length < 2) {
    return {
      kind: "none",
      reason: `${jsonFiles.length} JSON files, and their names do not identify runs. Pick the two exports yourself.`
    };
  }

  const byName = new Map(jsonFiles.map((file) => [file.name, file]));
  const suggested = newestPair(jsonFiles.map((file) => file.name))!;
  return {
    kind: "choose",
    runs: runs.map((run) => ({ file: byName.get(run.fileName)!, run })),
    suggested: {
      reference: byName.get(suggested.reference.fileName)!,
      candidate: byName.get(suggested.candidate.fileName)!
    }
  };
}
